import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
  CreateProductRequest,
  Product,
  ProductPriceResponse,
  ProductTask,
  SyncStatusResponse,
  UpdateProductRequest
} from '../../models/product';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../env';
import { AppLogger, AppLoggerService } from '../../core/logging';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private readonly log: AppLogger;

  constructor(private http: HttpClient, appLogger: AppLoggerService) {
    this.log = appLogger.createForClass(ProductService);
  }

  getAllProducts(
    householdId: number,
    options?: {
      stockStatus?: 'in' | 'low' | 'out';
      query?: string;
      categoryKey?: string;
      productKey?: string;
    }
  ): Observable<Product[]> {
    this.log.debug('Fetch all products..', {householdId, options});

    let params = new HttpParams();

    if (options) {
      const {stockStatus, query, categoryKey, productKey} = options;

      if (stockStatus) {
        params = params.set('stock_status', stockStatus);
      }

      if (query) {
        params = params.set('query', query);
      }

      if (categoryKey) {
        params = params.set('category_key', categoryKey);
      }

      if (productKey) {
        params = params.set('product_key', productKey);
      }
    }

    const hasParams = params.keys().length > 0;

    if (hasParams) {
      return this.http.get<Product[]>(`${environment.apiUrl}/households/${householdId}/products`, {params});
    }

    return this.http.get<Product[]>(`${environment.apiUrl}/households/${householdId}/products`);
  }

  /**
   * Fetch products that expire within an optional date range and limit.
   * Dates can be provided as a string (YYYY-MM-DD) or a Date object.
   */
  getExpiringProducts(householdId: number, from?: string | Date, to?: string | Date, limit?: number): Observable<Product[]> {
    this.log.debug('Fetch expiring products..', {householdId, from, to, limit});

    let params = new HttpParams();
    if (from) {
      const f = typeof from === 'string' ? from : this.toDateString(from);
      params = params.set('expires_from', f);
    }
    if (to) {
      const t = typeof to === 'string' ? to : this.toDateString(to);
      params = params.set('expires_to', t);
    }
    if (limit != null) {
      params = params.set('limit', String(limit));
    }

    return this.http.get<Product[]>(`${environment.apiUrl}/households/${householdId}/products`, {params});
  }

  /**
   * Fetch open products, optionally limited in number.
   */
  getOpenProducts(householdId: number, limit?: number): Observable<Product[]> {
    this.log.debug('Fetch open products..', {householdId, limit});

    let params = new HttpParams();
    params = params.set('is_open', 'true');
    if (limit != null) {
      params = params.set('limit', String(limit));
    }

    this.log.debug('Fetch open products..', {householdId, limit, params});

    return this.http.get<Product[]>(`${environment.apiUrl}/households/${householdId}/products`, {params});
  }

  private toDateString(d: Date): string {
    // produce YYYY-MM-DD
    return d.toISOString().slice(0, 10);
  }

  addProduct(householdId: number, req: CreateProductRequest): Observable<any> {
    return this.http.post<Product>(`${environment.apiUrl}/households/${householdId}/products`, req);
  }

  getProduct(productId: number): Observable<Product> {
    return this.http.get<Product>(`${environment.apiUrl}/products/${productId}`);
  }

  getProductSyncStatus(productId: number): Observable<SyncStatusResponse> {
    return this.http.get<SyncStatusResponse>(`${environment.apiUrl}/products/${productId}/sync-status`);
  }

  triggerProductSync(productId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/products/${productId}/sync`, {});
  }

  updateProduct(productId: number, req: UpdateProductRequest): Observable<string> {
    return this.http.put<string>(`${environment.apiUrl}/products/${productId}`, req);
  }

  deleteProduct(productId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/products/${productId}`);
  }

  deleteProductAttribute(productId: number, provider: string, name: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/products/${productId}/attributes/${provider}/${name}`);
  }

  setCodeAsPrimary(productId: number, codeValue: string): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/products/${productId}/codes/${encodeURIComponent(codeValue)}/primary`, null);
  }

  getProductPrice(productId: number): Observable<ProductPriceResponse> {
    return this.http.get<ProductPriceResponse>(`${environment.apiUrl}/products/${productId}/price`);
  }

  getActiveProductTasks(productId: number): Observable<ProductTask[]> {
    const params = new HttpParams().set('active', 'true');
    return this.http.get<ProductTask[]>(`${environment.apiUrl}/products/${productId}/tasks`, {params});
  }

}
